import { usePostHog } from "@posthog/react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
} from "react";
import { useLazyStaffStoreAppDetailsQuery } from "../../../api";
import { updateGlobalAPIBaseURLs } from "../../../api/axios/api-instances";
import { refreshAccessToken } from "../../../api/helpers/refresh-token";
import {
  subscribeToAutomaticLogout,
  type AutomaticLogoutContext,
} from "../../../shared/auth";
import { convertServicesToEnvironment } from "../../../shared/lib/environment";
import { getAppKey } from "../../../shared/lib/helpers";
import { installPathlessHistory } from "../../../shared/lib/pathless-history";
import { appSession } from "../../../shared/lib/storage";
import { useAppDispatch } from "../../../store";
import { clearSession, logout } from "../../../store/slices/authSlice";
import { createAutomaticLogoutProperties } from "./automatic-logout-event";
import {
  clearCustomerViewSession,
  startCustomerViewSession,
} from "./customer-view-session";
import {
  createKioskBinding,
  readKioskBinding,
  readLegacyKioskBinding,
  writeKioskBinding,
  type KioskBindingV1,
  type KioskOperator,
} from "./kiosk-binding";
import {
  escalateKioskIncident,
  openKioskIncident,
  recoverKioskIncidents,
} from "./kiosk-incident";
import {
  initialKioskRuntimeState,
  kioskRuntimeReducer,
  reportedCustomerView,
} from "./kiosk-runtime";
import type {
  KioskCameraDegradedContext,
  KioskCustomerView,
} from "../../../views/mobile-app-checkout";

const HEARTBEAT_INTERVAL_MS = 5 * 60_000;
const STAFF_FLOW_IDLE_TIMEOUT_MS = 2 * 60_000;

type SessionInvalidContext = {
  operation: string;
  reason: "credentials_missing" | "refresh_rejected";
  status?: number;
};

type ServiceFailureContext = {
  operation: "available_services" | "current_shift";
  status?: number;
  kind: "permission" | "network" | "http" | "protocol";
};

type StreamFailureContext = {
  operation: "scan_stream";
  retryCount: number;
  status?: number;
};

function getOperator(): KioskOperator | null {
  const staff = appSession.getItem()?.staff;
  return staff ? { userId: staff.userId, fullName: staff.fullName } : null;
}

function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const value = error as {
    status?: number;
    response?: { status?: number };
  };
  return value.status ?? value.response?.status;
}

export function useKioskRuntimeController() {
  const reduxDispatch = useAppDispatch();
  const posthog = usePostHog();
  const [state, dispatch] = useReducer(
    kioskRuntimeReducer,
    initialKioskRuntimeState,
  );
  const legacyBindingRef = useRef(readLegacyKioskBinding());
  const currentBindingRef = useRef<KioskBindingV1 | null>(null);
  const migrationTimerRef = useRef<number>();
  const serviceEscalationTimerRef = useRef<number>();
  const serviceIncidentRef = useRef<{ code: string; id: string } | null>(null);
  const migrationAttemptRef = useRef(0);
  const automaticLogoutHandledRef = useRef(false);
  const customerViewRef = useRef<KioskCustomerView | null>(null);
  const customerViewSinceRef = useRef<number | null>(null);
  const wasOperationalRef = useRef(false);
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();

  const reportIncident = useCallback(
    (
      lifecycle: "opened" | "recovered",
      code: string,
      extra: Record<string, unknown> = {},
    ) => {
      const operator = getOperator();
      const binding = currentBindingRef.current;
      const baseProperties = {
        lifecycle,
        severity: lifecycle === "opened" ? "critical" : "info",
        incident_code: code,
        kiosk_installation_id: binding?.kioskInstallationId,
        store_key: binding?.storeKey,
        zone_id: binding?.zoneId,
        placement: binding?.placement,
        app_version: import.meta.env.VITE_APP_VERSION,
        online: navigator.onLine,
        user_id: operator?.userId,
        user_full_name: operator?.fullName,
        configured_by_user_id: binding?.configuredBy?.userId,
        configured_by_user_full_name: binding?.configuredBy?.fullName,
        ...extra,
      };

      if (lifecycle === "opened") {
        const operation = String(extra.operation ?? "kiosk_runtime");
        const { incident, created } = openKioskIncident(code, operation);
        if (!created) return incident;
        posthog.capture("kiosk_incident", {
          ...baseProperties,
          incident_id: incident.id,
          occurrence_count: incident.occurrences,
          opened_at: new Date(incident.openedAt).toISOString(),
        });
        return incident;
      }

      recoverKioskIncidents(code).forEach((incident) => {
        posthog.capture("kiosk_incident", {
          ...baseProperties,
          incident_id: incident.id,
          operation: incident.operation,
          occurrence_count: incident.occurrences,
          outage_duration_ms: incident.durationMs,
        });
      });
      return undefined;
    },
    [posthog],
  );

  const becomeOperational = useCallback(
    (binding: KioskBindingV1) => {
      automaticLogoutHandledRef.current = false;
      updateGlobalAPIBaseURLs(binding.services, binding.storeKey);
      writeKioskBinding(binding);
      currentBindingRef.current = binding;
      dispatch({ type: "BECOME_OPERATIONAL", binding });
      recoverKioskIncidents().forEach((incident) => {
        const operator = getOperator();
        posthog.capture("kiosk_incident", {
          lifecycle: "recovered",
          severity: "info",
          incident_id: incident.id,
          incident_code: incident.code,
          operation: incident.operation,
          occurrence_count: incident.occurrences,
          outage_duration_ms: incident.durationMs,
          kiosk_installation_id: binding.kioskInstallationId,
          store_key: binding.storeKey,
          zone_id: binding.zoneId,
          placement: binding.placement,
          user_id: operator?.userId,
          user_full_name: operator?.fullName,
        });
      });
    },
    [posthog],
  );

  const validateBindingForSession = useCallback(
    async (binding: KioskBindingV1) => {
      dispatch({ type: "START_RECOVERY", reason: "validating_staff_access" });
      try {
        const appDetails = await getAppDetails({
          store_key: binding.storeKey,
          app_key: getAppKey(),
        }).unwrap();
        await refreshAccessToken({
          storeKey: binding.storeKey,
          reason: "bootstrap",
        });
        const services = convertServicesToEnvironment(appDetails.result.services);
        becomeOperational({ ...binding, services });
      } catch (error) {
        const status = getHttpStatus(error);
        if (status === 403) {
          reportIncident("opened", "permission_denied", {
            operation: "kiosk_binding_validation",
            http_status: status,
          });
          dispatch({
            type: "PERMISSION_DENIED",
            reason: "staff_account_not_authorized",
          });
          return;
        }
        reportIncident("opened", "kiosk_binding_validation_failed", {
          operation: "kiosk_binding_validation",
          http_status: status,
        });
        dispatch({
          type: "GO_OUT_OF_SERVICE",
          reason: "binding_validation_failed",
        });
      }
    },
    [becomeOperational, getAppDetails, reportIncident],
  );

  const migrateLegacyBinding = useCallback(async () => {
    const legacy = legacyBindingRef.current;
    const session = appSession.getItem();
    if (!legacy || !session) {
      dispatch({ type: "BOOT_WITHOUT_BINDING" });
      return;
    }

    dispatch({ type: "START_RECOVERY", reason: "migrating_legacy_url" });
    try {
      const appDetails = await getAppDetails({
        store_key: legacy.storeKey,
        app_key: getAppKey(),
      }).unwrap();
      await refreshAccessToken({
        storeKey: legacy.storeKey,
        reason: "bootstrap",
      });
      const services = convertServicesToEnvironment(appDetails.result.services);
      const binding = createKioskBinding({
        storeKey: legacy.storeKey,
        storeName: legacy.storeKey,
        zoneId: legacy.zoneId,
        zoneName: legacy.zoneId,
        placement: legacy.placement,
        services,
        configuredBy: getOperator(),
      });
      legacyBindingRef.current = null;
      becomeOperational(binding);
    } catch (error) {
      if (getHttpStatus(error) === 403) {
        reportIncident("opened", "permission_denied", {
          operation: "legacy_kiosk_migration",
          http_status: 403,
        });
        dispatch({ type: "PERMISSION_DENIED", reason: "legacy_access_denied" });
        return;
      }
      migrationAttemptRef.current += 1;
      const delay = Math.min(
        30_000,
        1_000 * 2 ** Math.min(migrationAttemptRef.current, 5),
      );
      migrationTimerRef.current = window.setTimeout(migrateLegacyBinding, delay);
    }
  }, [becomeOperational, getAppDetails, reportIncident]);

  useEffect(() => {
    const binding = readKioskBinding();
    const removeHistoryGuard = installPathlessHistory();

    if (binding) {
      currentBindingRef.current = binding;
      updateGlobalAPIBaseURLs(binding.services, binding.storeKey);
      if (appSession.getItem()) {
        dispatch({ type: "BOOT_WITH_BINDING", binding });
      } else {
        dispatch({ type: "BOOT_SIGNED_OUT_WITH_BINDING", binding });
      }
    } else {
      void migrateLegacyBinding();
    }

    return () => {
      removeHistoryGuard();
      window.clearTimeout(migrationTimerRef.current);
      window.clearTimeout(serviceEscalationTimerRef.current);
    };
  }, [migrateLegacyBinding]);

  useEffect(() => {
    if (!state.binding) return;
    posthog.register({
      kiosk_installation_id: state.binding.kioskInstallationId,
      store_key: state.binding.storeKey,
      zone_id: state.binding.zoneId,
      placement: state.binding.placement,
      app_version: import.meta.env.VITE_APP_VERSION,
    });
    posthog.identify(state.binding.kioskInstallationId, {
      store_key: state.binding.storeKey,
      zone_id: state.binding.zoneId,
      placement: state.binding.placement,
    });

    const heartbeat = () => {
      const operator = getOperator();
      const customerView = reportedCustomerView(
        state.phase,
        customerViewRef.current,
      );
      const since =
        customerView === "none" ? null : customerViewSinceRef.current;
      posthog.capture("kiosk_heartbeat", {
        runtime_state: state.phase,
        // The runtime phase reads "operational" for a kiosk showing
        // maintenance, out-of-service, a loading spinner, or a scan-stream
        // outage. Alerting needs the screen the customer is actually facing, and
        // how long it has been there, to tell a shift change from a stuck kiosk.
        customer_view: customerView,
        customer_view_duration_ms: since === null ? undefined : Date.now() - since,
        online: navigator.onLine,
        user_id: operator?.userId,
        user_full_name: operator?.fullName,
      });
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [posthog, state.binding, state.phase]);

  useEffect(() => {
    if (
      state.phase !== "staff-login" &&
      state.phase !== "staff-resume-choice" &&
      state.phase !== "staff-setup"
    )
      return;

    let timer = window.setTimeout(() => {
      dispatch({ type: "GO_OUT_OF_SERVICE", reason: "staff_flow_timed_out" });
    }, STAFF_FLOW_IDLE_TIMEOUT_MS);
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        dispatch({ type: "GO_OUT_OF_SERVICE", reason: "staff_flow_timed_out" });
      }, STAFF_FLOW_IDLE_TIMEOUT_MS);
    };

    window.addEventListener("pointerdown", reset);
    window.addEventListener("keydown", reset);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", reset);
      window.removeEventListener("keydown", reset);
    };
  }, [state.phase]);

  const handleAutomaticLogout = useCallback(
    (context: AutomaticLogoutContext) => {
      if (automaticLogoutHandledRef.current) return;
      automaticLogoutHandledRef.current = true;
      const operator = getOperator();
      const binding = currentBindingRef.current;

      posthog.capture("kiosk_automatic_logout", {
        ...createAutomaticLogoutProperties(context, binding, operator),
        app_version: import.meta.env.VITE_APP_VERSION,
        online: navigator.onLine,
      });
      reportIncident("opened", "credentials_invalid", {
        operation: context.operation,
        http_status: context.status,
        automatic_logout_reason: context.reason,
      });
      reduxDispatch(clearSession());
      dispatch({ type: "GO_OUT_OF_SERVICE", reason: "credentials_invalid" });
    },
    [posthog, reduxDispatch, reportIncident],
  );

  useLayoutEffect(
    () => subscribeToAutomaticLogout(handleAutomaticLogout),
    [handleAutomaticLogout],
  );

  const onPermissionDenied = useCallback(
    ({ operation }: { operation: string; status: 403 }) => {
      reportIncident("opened", "permission_denied", {
        operation,
        http_status: 403,
      });
      dispatch({ type: "PERMISSION_DENIED", reason: operation });
    },
    [reportIncident],
  );

  const onSessionInvalid = useCallback(
    (context: SessionInvalidContext) => handleAutomaticLogout(context),
    [handleAutomaticLogout],
  );

  const onServiceFailure = useCallback(
    ({ operation, status, kind }: ServiceFailureContext) => {
      if (kind === "permission") return;
      const code = `${operation}_${kind}_failure`;
      const incident = reportIncident("opened", code, {
        operation,
        http_status: status,
        failure_kind: kind,
        severity: "warning",
      });
      if (!incident) return;

      serviceIncidentRef.current = { code, id: incident.id };
      window.clearTimeout(serviceEscalationTimerRef.current);
      serviceEscalationTimerRef.current = window.setTimeout(() => {
        const escalated = escalateKioskIncident(incident.id);
        if (!escalated) return;
        const binding = currentBindingRef.current;
        const operator = getOperator();
        posthog.capture("kiosk_incident", {
          lifecycle: "escalated",
          severity: "critical",
          incident_id: escalated.id,
          incident_code: escalated.code,
          operation: escalated.operation,
          occurrence_count: escalated.occurrences,
          outage_duration_ms: Date.now() - escalated.openedAt,
          http_status: status,
          failure_kind: kind,
          kiosk_installation_id: binding?.kioskInstallationId,
          store_key: binding?.storeKey,
          zone_id: binding?.zoneId,
          placement: binding?.placement,
          user_id: operator?.userId,
          user_full_name: operator?.fullName,
        });
      }, 60_000);
    },
    [posthog, reportIncident],
  );

  const onServiceRecovered = useCallback(() => {
    window.clearTimeout(serviceEscalationTimerRef.current);
    const active = serviceIncidentRef.current;
    serviceIncidentRef.current = null;
    if (active) reportIncident("recovered", active.code);
  }, [reportIncident]);

  const onCustomerViewChange = useCallback(
    (view: KioskCustomerView) => {
      const previous = customerViewRef.current;
      if (previous === view) return;
      const since = customerViewSinceRef.current;
      customerViewRef.current = view;
      // A stored session for this same view keeps its original start time, so a
      // screen that outlives one or more self-inflicted reloads reports how long
      // the kiosk has been stuck rather than how long this page has been open.
      customerViewSinceRef.current = startCustomerViewSession(
        view,
        Date.now(),
      ).since;
      posthog.capture("kiosk_customer_view", {
        customer_view: view,
        previous_customer_view: previous ?? "none",
        previous_view_duration_ms:
          since === null ? undefined : Date.now() - since,
        online: navigator.onLine,
      });
    },
    [posthog],
  );

  useEffect(() => {
    if (state.phase === "operational") {
      wasOperationalRef.current = true;
      return;
    }
    // Only clear when leaving the checkout, never merely because the phase is
    // not operational. Boot starts in `booting`, so clearing on any
    // non-operational phase would wipe the stored session on every reload and
    // defeat the point of persisting it.
    if (!wasOperationalRef.current) return;
    wasOperationalRef.current = false;
    // Clearing on the way out keeps a screen from the previous operational
    // session out of the first heartbeat of the next one, before the remounted
    // checkout has reported anything. The heartbeat gates on the phase too, so
    // it never depends on this having run.
    customerViewRef.current = null;
    customerViewSinceRef.current = null;
    clearCustomerViewSession();
  }, [state.phase]);

  const onStreamFailure = useCallback(
    ({ operation, retryCount, status }: StreamFailureContext) => {
      reportIncident("opened", "scan_stream_unavailable", {
        operation,
        http_status: status,
        retry_count: retryCount,
      });
    },
    [reportIncident],
  );

  const onStreamRecovered = useCallback(() => {
    reportIncident("recovered", "scan_stream_unavailable");
  }, [reportIncident]);

  const onCameraDegraded = useCallback(
    (context: KioskCameraDegradedContext) => {
      reportIncident("opened", "face_gate_camera_degraded", {
        operation: context.operation,
        failure_stage: context.stage,
        failure_reason: context.reason,
        retry_count: context.retryCount,
        staff_action_required: context.staffActionRequired,
        severity: "warning",
      });
    },
    [reportIncident],
  );

  const onCameraRecovered = useCallback(() => {
    reportIncident("recovered", "face_gate_camera_degraded");
  }, [reportIncident]);

  const onStaffAccess = useCallback(() => {
    if (!appSession.getItem()) {
      dispatch({ type: "OPEN_STAFF_LOGIN" });
    } else if (state.binding) {
      dispatch({ type: "OPEN_STAFF_RESUME_CHOICE" });
    } else {
      dispatch({ type: "OPEN_STAFF_SETUP" });
    }
  }, [state.binding]);

  const onAuthenticated = useCallback(() => {
    automaticLogoutHandledRef.current = false;
    if (state.binding) dispatch({ type: "OPEN_STAFF_RESUME_CHOICE" });
    else dispatch({ type: "OPEN_STAFF_SETUP" });
  }, [state.binding]);

  const onStaffCancel = useCallback(() => {
    dispatch({ type: "GO_OUT_OF_SERVICE", reason: "cancelled" });
  }, []);

  const onSetupCancel = useCallback(() => {
    if (state.binding) {
      dispatch({ type: "BOOT_WITH_BINDING", binding: state.binding });
    } else {
      dispatch({ type: "BOOT_WITHOUT_BINDING" });
    }
  }, [state.binding]);

  const onLogout = useCallback(async () => {
    try {
      await reduxDispatch(logout()).unwrap();
    } finally {
      reduxDispatch(clearSession());
      dispatch({ type: "GO_OUT_OF_SERVICE", reason: "signed_out" });
    }
  }, [reduxDispatch]);

  return {
    state,
    operator: getOperator(),
    locked: ![
      "operational",
      "staff-login",
      "staff-resume-choice",
      "staff-setup",
    ].includes(state.phase),
    onStaffAccess,
    onAuthenticated,
    onContinueSavedBinding: validateBindingForSession,
    onOpenSetup: () => dispatch({ type: "OPEN_STAFF_SETUP" }),
    onStaffCancel,
    onSetupComplete: becomeOperational,
    onSetupCancel,
    onPermissionDenied,
    onSessionInvalid,
    onServiceFailure,
    onServiceRecovered,
    onStreamFailure,
    onStreamRecovered,
    onCameraDegraded,
    onCameraRecovered,
    onCustomerViewChange,
    onLogout,
  };
}
